<template>
  <svg v-if="moulding" :width="`${totalWidth}in`" :height="`${totalHeight}in`" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <!-- Define drop shadow filter with filterUnits="userSpaceOnUse" -->
      <filter :id="`${uuid}-mouldingStrutDropShadow`" x="0" y="0" :width="totalWidth" :height="totalHeight" filterUnits="userSpaceOnUse">
        <feDropShadow dx="0" dy="0.1" stdDeviation="0.3" flood-color="rgba(0, 0, 0, 0.5)" />
      </filter>

      <!-- Define masks for mitered corners -->
      <mask :id="`${uuid}-miter-mask-top`">
        <rect width="100%" height="100%" fill="white"/>
        <polygon :points="`0,0 ${faceWidth},${faceWidth} 0,${faceWidth}`" fill="black"/>
        <polygon :points="`${totalWidth},0 ${totalWidth - faceWidth},${faceWidth} ${totalWidth},${faceWidth}`" fill="black"/>
      </mask>

      <mask :id="`${uuid}-miter-mask-right`">
        <rect width="100%" height="100%" fill="white"/>
        <polygon :points="`${totalWidth},0 ${totalWidth - faceWidth},${faceWidth} ${totalWidth - faceWidth},0`" fill="black"/>
        <polygon :points="`${totalWidth},${totalHeight} ${totalWidth - faceWidth},${totalHeight - faceWidth} ${totalWidth - faceWidth},${totalHeight}`" fill="black"/>
      </mask>

      <mask :id="`${uuid}-miter-mask-bottom`">
        <rect width="100%" height="100%" fill="white"/>
        <polygon :points="`0,${totalHeight} ${faceWidth},${totalHeight - faceWidth} 0,${totalHeight - faceWidth}`" fill="black"/>
        <polygon :points="`${totalWidth},${totalHeight} ${totalWidth - faceWidth},${totalHeight - faceWidth} ${totalWidth},${totalHeight - faceWidth}`" fill="black"/>
      </mask>

      <mask :id="`${uuid}-miter-mask-left`">
        <rect width="100%" height="100%" fill="white"/>
        <polygon :points="`0,0 ${faceWidth},${faceWidth} ${faceWidth},0`" fill="black"/>
        <polygon :points="`0,${totalHeight} ${faceWidth},${totalHeight - faceWidth} ${faceWidth},${totalHeight}`" fill="black"/>
      </mask>

      <!-- Define patterns for surface tiling -->
      <pattern :id="`${uuid}-tile-pattern-top`" patternUnits="userSpaceOnUse" :width="tileWidth" :height="tileHeight">
        <image :href="moulding.tiles[0].image_url" :width="tileWidth" :height="tileHeight" preserveAspectRatio="none" />
      </pattern>

      <pattern :id="`${uuid}-tile-pattern-right`" patternUnits="userSpaceOnUse" :width="tileWidth" :height="tileHeight" :x="`${totalWidth - faceWidth || 0}`" y="0" :patternTransform="`rotate(90, ${totalWidth - faceWidth || 0}, 0)`" >
        <image :href="moulding.tiles[0].image_url" :width="tileWidth" :height="tileHeight" preserveAspectRatio="none" />
      </pattern>

      <pattern :id="`${uuid}-tile-pattern-bottom`" patternUnits="userSpaceOnUse" :width="tileWidth" :height="tileHeight" x="0" :y="`${totalHeight - faceWidth}`" :patternTransform="`rotate(180, 0, ${totalHeight - faceWidth})`">
        <image :href="moulding.tiles[0].image_url" :width="tileWidth" :height="tileHeight" preserveAspectRatio="none" />
      </pattern>

      <pattern :id="`${uuid}-tile-pattern-left`" patternUnits="userSpaceOnUse" :width="tileWidth" :height="tileHeight" patternTransform="rotate(-90)">
        <image :href="moulding.tiles[0].image_url" :width="tileWidth" :height="tileHeight" preserveAspectRatio="none" />
      </pattern>
    </defs>

    <!-- Render the four struts with mitered corners -->
    <rect
      class="render-svg-moulding-strut-top"
      :x="0"
      :y="0"
      :width="totalWidth"
      :height="faceWidth"
      :style="mouldingStrutDropShadow"
      :fill="`url(#${uuid}-tile-pattern-top)`"
    />

    <rect
      class="render-svg-moulding-strut-right"
      :x="totalWidth - faceWidth"
      :y="0"
      :width="faceWidth"
      :height="totalHeight"
      :mask="`url(#${uuid}-miter-mask-right)`"
      :fill="`url(#${uuid}-tile-pattern-right)`"
    />

    <rect
      class="render-svg-moulding-strut-bottom"
      :x="0"
      :y="totalHeight - faceWidth"
      :width="totalWidth"
      :height="faceWidth"
      :mask="`url(#${uuid}-miter-mask-bottom)`"
      :fill="`url(#${uuid}-tile-pattern-bottom)`"
    />

    <rect
      class="render-svg-moulding-strut-left"
      :x="0"
      :y="0"
      :width="faceWidth"
      :height="totalHeight"
      :mask="`url(#${uuid}-miter-mask-left)`"
      :fill="`url(#${uuid}-tile-pattern-left)`"
    />

    <!-- Render the brass plate if it exists -->
    <RenderSvgMouldingPlate
      v-if="mouldingPlate"
      :mouldingPlate="mouldingPlate"
      :mouldingPlateX="mouldingPlateX"
      :mouldingPlateY="mouldingPlateY"
      :mouldingPlateWidth="mouldingPlateWidth"
      :mouldingPlateHeight="mouldingPlateHeight"
    />

    <RenderSvgMouldingEngraving
      v-if="mouldingEngraving"
      :frameSpec="frameSpec"
      :mouldingEngraving="mouldingEngraving"
      :bottomMouldingY="bottomMouldingY"
      :mouldingEngravingWidth="totalWidth"
      :mouldingEngravingHeight="faceWidth"
    />
  </svg>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import uuidv4 from "uuid/v4";
import RenderSvgMouldingPlate from './RenderSvgMouldingPlate.vue';
import RenderSvgMouldingEngraving from './RenderSvgMouldingEngraving.vue';

export default {
  components: {
    RenderSvgMouldingPlate,
    RenderSvgMouldingEngraving
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    moulding: {
      type: Object,
      required: true
    }
  },
  computed: {
    fullMat () {
      return this.frameSpec.getFullPrimaryMat();
    },
    uuid() {
      return uuidv4();
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvasOrCanvasPaper();
    },
    mouldingStrutDropShadow() {
      return `filter: url(#${this.uuid}-mouldingStrutDropShadow);`;
    },
    artwork() {
      return this.frameSpec.getArtwork(0);
    },
    mat() {
      return this.frameSpec.mats[0] || {};
    },
    totalsOfAllMatRevealsOrMountReveals() {
      if (!isEmpty(this.mat)) {
        return {
          topInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
          rightInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
          bottomInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
          leftInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
        };
      } else if (this.frameSpec.isFloated() || this.frameSpec.isClearFloated()) {
        return this.frameSpec.mount.reveal;
      } else {
        return {
          topInInches: 0,
          rightInInches: 0,
          bottomInInches: 0,
          leftInInches: 0
        };
      }
    },
    mountedArtworkWidth() {
      return this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsOrMountReveals.leftInInches + this.totalsOfAllMatRevealsOrMountReveals.rightInInches;
    },
    mountedArtworkHeight() {
      return this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsOrMountReveals.topInInches + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches;
    },
    faceWidth() {
      if (!this.moulding) return 0;
      // The moulding tile images for canvas mouldings include the canvas gap width in the image.
      const canvasGapWidth = this.moulding.canvas_gap_width || 0;
      return this.moulding.face_width + canvasGapWidth;
    },
    rearWidth() {
      return this.moulding.rear_width;
    },
    totalWidth() {
      if (this.isCanvasMoulding) {
        return this.mountedArtworkWidth + 2 * (this.faceWidth || 0);
      }

      if (this.fullMat && this.fullMat.matWidth) {
        return this.fullMat.matWidth + 2 * (this.rearWidth || 0);
      }

      return this.mountedArtworkWidth + 2 * (this.rearWidth || 0);
    },
    totalHeight() {
      if (this.isCanvasMoulding) {
        return this.mountedArtworkHeight + 2 * (this.faceWidth || 0);
      }

      if (this.fullMat && this.fullMat.matHeight) {
        return this.fullMat.matHeight + 2 * (this.rearWidth || 0);
      }
      
      return this.mountedArtworkHeight + 2 * (this.rearWidth || 0);
    },
    tileHeight() {
      return this.faceWidth;
    },
    tileWidth() {
      const aspectRatio = this.moulding.tiles[0].image_metadata.original.width / this.moulding.tiles[0].image_metadata.original.height;
      return this.tileHeight * aspectRatio;
    },
    mouldingPlate() {
      return this.frameSpec.moulding.mouldingPlate;
    },
    mouldingPlateWidth() {
      return 2.36; // 2.36 inches
    },
    mouldingPlateHeight() {
      return 0.51; // 0.51 inches
    },
    mouldingPlateX() {
      return (this.totalWidth - this.mouldingPlateWidth) / 2;
    },
    mouldingPlateY() {
      return this.totalHeight - this.faceWidth + (this.faceWidth - this.mouldingPlateHeight) / 2;
    },
    mouldingEngraving() {
      return this.frameSpec.moulding?.mouldingEngraving;
    },
    bottomMouldingY() {
      return this.totalHeight - this.faceWidth;
    },
  }
};
</script>

<style scoped>
/* No styles needed */
</style>